import React from "react";
import { motion } from "framer-motion";
import { Search, DraftingCompass, Rocket, LineChart } from "lucide-react";

const steps = [
  {
    testid: "process-step-1",
    num: "01",
    icon: Search,
    title: "Discovery & AI Audit",
    description:
      "We map your operations, data and systems to find where intelligence creates the most leverage — and what it is worth.",
  },
  {
    testid: "process-step-2",
    num: "02",
    icon: DraftingCompass,
    title: "Architecture & Blueprint",
    description:
      "A concrete technical and financial plan: stack decisions, integration points, timeline and the ROI model behind every milestone.",
  },
  {
    testid: "process-step-3",
    num: "03",
    icon: Rocket,
    title: "Agile Implementation",
    description:
      "Senior engineers ship in tight cycles with weekly demos. A working prototype inside 14 days, production systems inside the quarter.",
  },
  {
    testid: "process-step-4",
    num: "04",
    icon: LineChart,
    title: "Scale & ROI Optimization",
    description:
      "We stay through the numbers: monitoring, iteration and growth loops until the projected return is a recorded one.",
  },
];

export const Process = () => (
  <section
    id="process"
    data-testid="process-section"
    className="relative py-20 lg:py-32 bg-[#FAFAFC]"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-2xl mb-14 lg:mb-20"
      >
        <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#0FA89E] font-semibold mb-4">
          How we work
        </p>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A] mb-5">
          From "how do we fit in?" to "built around us"
        </h2>
        <p className="text-lg text-slate-600 leading-relaxed">
          Four phases. No black boxes, no endless discovery theatre — and no
          force-fitting your team into someone else's workflow.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {steps.map((step, i) => {
          const Icon = step.icon;
          return (
            <motion.div
              key={step.testid}
              data-testid={step.testid}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="group relative rounded-2xl border border-slate-200 bg-white p-7 lg:p-8 transition-all duration-300 hover:border-[rgba(0,82,255,0.35)] hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(15,23,42,0.15)]"
            >
              <div className="flex items-center justify-between mb-8">
                <span className="font-mono-tech text-sm font-semibold text-[#0FA89E]">
                  {step.num}
                </span>
                <Icon className="w-6 h-6 text-slate-400 transition-colors group-hover:text-[#0FA89E]" />
              </div>
              <h3 className="font-display text-xl font-semibold tracking-tight text-[#0F172A] mb-3">
                {step.title}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {step.description}
              </p>
              <div className="absolute bottom-0 left-7 right-7 h-[2px] bg-[#0FA89E] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 rounded-full" />
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);
